import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCredits } from '../contexts/CreditContext';
import { CREDITS_PER_DOLLAR, MIN_PURCHASE_AMOUNT, MAX_PURCHASE_AMOUNT, adminGrantCredits } from '../model/CreditUtils';
import { getSubscriptionInfo, cancelSubscription, createSubscriptionCheckoutSession, clearSubscriptionCheckoutLock, SubscriptionInfo, PRO_MONTHLY_PRICE, STRIPE_PRO_MONTHLY_PRICE_ID } from '../model/SubscriptionUtils';
import { auth } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import styles from './BalancePage.module.css';
import { GAUtils } from '../model/GAUtils';
import { useRewardedAd } from '../components/RewardedAd';

const ADMIN_EMAILS = ['jasonlie7@gmail.com'];
const PRESET_AMOUNTS = [1, 5, 10, 25, 50, 100];
const DISCOUNT_THRESHOLD = 10;
const DISCOUNT_RATE = 0.1;

type TabType = 'pro' | 'credits';

const BalancePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { credits, loading, refreshCredits, purchaseCredits } = useCredits();
  const presetAmount = parseInt(searchParams.get('amount') || '');
  const [selectedAmount, setSelectedAmount] = useState<number>(
    !isNaN(presetAmount) && presetAmount >= 1 ? presetAmount : 10
  );
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isCustom, setIsCustom] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>(searchParams.get('tab') === 'pro' ? 'pro' : 'credits');
  const [subInfo, setSubInfo] = useState<SubscriptionInfo | null>(null);
  const [, setSubLoading] = useState(true);
  const [isCanceling, setIsCanceling] = useState(false);
  const [adMessage, setAdMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [adminAmount, setAdminAmount] = useState<string>('10');
  const [isGranting, setIsGranting] = useState(false);

  const handleAdReward = useCallback(() => {
    refreshCredits();
    setAdMessage({ type: 'success', text: 'You earned 1 free credit!' });
    setTimeout(() => setAdMessage(null), 4000);
  }, [refreshCredits]);

  const handleAdError = useCallback((error: string) => {
    setAdMessage({ type: 'error', text: error });
    setTimeout(() => setAdMessage(null), 4000);
  }, []);

  const { showRewardedAd, isLoading: adLoading } = useRewardedAd(handleAdReward, handleAdError);

  useEffect(() => {
    GAUtils.trackPageView('balance', 'Credit Balance - XTEA');
    refreshCredits();
    loadSubscription();
  }, [refreshCredits]);

  useEffect(() => {
    if (!loading) {
      GAUtils.trackBalancePageView(credits);
    }
  }, [loading, credits]);

  useEffect(() => {
    if (!loading) {
      if (activeTab === 'pro') {
        GAUtils.trackSubscriptionViewItem(PRO_MONTHLY_PRICE);
      } else {
        GAUtils.trackViewItem(10, getCreditsForAmount(10));
      }
    }
  }, [loading, activeTab]);

  const loadSubscription = async () => {
    const user = auth.currentUser;
    if (!user) { setSubLoading(false); return; }
    try {
      const info = await getSubscriptionInfo(user.uid);
      setSubInfo(info);
    } catch (e) {
      console.error('Error loading subscription:', e);
    } finally {
      setSubLoading(false);
    }
  };

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    const type = searchParams.get('type'); // 'subscription' or undefined (credits)
    if (searchParams.get('success') === 'true') {
      if (type === 'subscription') {
        clearSubscriptionCheckoutLock();
        GAUtils.trackSubscribeSuccess(PRO_MONTHLY_PRICE);
        setMessage({ type: 'success', text: 'Pro subscription activated! Enjoy unlimited searches.' });
        setActiveTab('pro');
        loadSubscription();
      } else {
        const creditsAdded = searchParams.get('credits');
        const amount = searchParams.get('amount');
        const purchaseAmount = amount ? parseFloat(amount) : 0;
        const purchaseCreditsNum = creditsAdded ? parseInt(creditsAdded, 10) : 0;
        GAUtils.trackPurchaseSuccess(purchaseAmount, purchaseCreditsNum);
        setMessage({ type: 'success', text: `Successfully purchased ${creditsAdded} credits! Your balance has been updated.` });
        refreshCredits();
      }
      navigate('/balance', { replace: true });
    } else if (searchParams.get('canceled') === 'true') {
      if (type === 'subscription') {
        clearSubscriptionCheckoutLock();
        GAUtils.trackSubscribeCanceled();
        setMessage({ type: 'error', text: 'Subscription checkout was canceled. You were not charged.' });
        setActiveTab('pro');
      } else {
        GAUtils.trackPurchaseCanceled();
        setMessage({ type: 'error', text: 'Purchase was canceled. You were not charged.' });
      }
      navigate('/balance', { replace: true });
    }
  }, [searchParams, navigate, refreshCredits]);

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
    GAUtils.trackPurchaseAmountSelect(amount, getCreditsForAmount(amount), false);
    GAUtils.trackAddToCart(amount, getCreditsForAmount(amount));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setCustomAmount(value);
      setIsCustom(true);
      if (value) {
        const amount = parseInt(value);
        setSelectedAmount(amount);
        GAUtils.trackPurchaseAmountSelect(amount, getCreditsForAmount(amount), true);
        GAUtils.trackAddToCart(amount, getCreditsForAmount(amount));
      }
    }
  };

  const getCurrentAmount = () => isCustom && customAmount ? parseInt(customAmount) : selectedAmount;
  const getCreditsForAmount = (amount: number) => amount * CREDITS_PER_DOLLAR;
  const getDiscountForAmount = (amount: number) => amount >= DISCOUNT_THRESHOLD ? amount * DISCOUNT_RATE : 0;
  const getFinalAmount = (amount: number) => amount - getDiscountForAmount(amount);
  const isValidAmount = () => {
    const amount = getCurrentAmount();
    return amount >= MIN_PURCHASE_AMOUNT && amount <= MAX_PURCHASE_AMOUNT;
  };

  const handlePurchase = async () => {
    const amount = getCurrentAmount();
    const creditsToGet = getCreditsForAmount(amount);
    if (!isValidAmount()) {
      GAUtils.trackPurchaseError(amount, `Invalid amount: must be between $${MIN_PURCHASE_AMOUNT} and $${MAX_PURCHASE_AMOUNT}`);
      setMessage({ type: 'error', text: `Amount must be between $${MIN_PURCHASE_AMOUNT} and $${MAX_PURCHASE_AMOUNT}.` });
      return;
    }
    GAUtils.trackPurchaseAttempt(amount, creditsToGet);
    GAUtils.trackBeginCheckout(amount, creditsToGet);
    setIsPurchasing(true);
    setMessage(null);
    try {
      const url = await purchaseCredits(amount);
      if (url) { window.location.href = url; }
      else {
        GAUtils.trackPurchaseError(amount, 'Failed to create checkout session');
        setMessage({ type: 'error', text: 'Failed to create checkout session. Please try again.' });
      }
    } catch (error: any) {
      console.error('Purchase error:', error);
      GAUtils.trackPurchaseError(amount, error.message || 'Unknown error');
      setMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally { setIsPurchasing(false); }
  };

  const handleSubscribe = async () => {
    GAUtils.trackSubscribeClick(PRO_MONTHLY_PRICE);
    const user = auth.currentUser;
    if (!user || !user.email) {
      GAUtils.trackSubscribeError('User not signed in');
      setMessage({ type: 'error', text: 'Please sign in first.' });
      return;
    }
    GAUtils.trackSubscribeBeginCheckout(PRO_MONTHLY_PRICE);
    setIsPurchasing(true);
    setMessage(null);
    try {
      const result = await createSubscriptionCheckoutSession(user.uid, user.email, STRIPE_PRO_MONTHLY_PRICE_ID);
      if (result.url) {
        window.location.href = result.url;
      } else {
        GAUtils.trackSubscribeError('Failed to create checkout session');
        setMessage({ type: 'error', text: 'Failed to create checkout session.' });
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      GAUtils.trackSubscribeError(error.message || 'Unknown error');
      const isAlreadySubscribed = error.message?.includes('already has an active') ||
        error.code === 'already-exists';
      setMessage({
        type: 'error',
        text: isAlreadySubscribed
          ? 'You already have an active Pro subscription.'
          : 'An error occurred. Please try again.',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subInfo?.subscription?.subscriptionId) return;
    const user = auth.currentUser;
    if (!user) return;
    if (!window.confirm('Are you sure you want to cancel your Pro subscription?')) return;

    GAUtils.trackSubscriptionCancelClick();
    setIsCanceling(true);
    try {
      await cancelSubscription(subInfo.subscription.subscriptionId, user.uid);
      GAUtils.trackSubscriptionCancelSuccess();
      setMessage({ type: 'success', text: 'Subscription canceled successfully.' });
      loadSubscription();
    } catch (e: any) {
      GAUtils.trackSubscriptionCancelError(e.message || 'Unknown error');
      setMessage({ type: 'error', text: 'Failed to cancel subscription. Please try again.' });
    } finally { setIsCanceling(false); }
  };

  const isAdmin = ADMIN_EMAILS.includes(auth.currentUser?.email || '');

  const handleAdminGrant = async () => {
    const user = auth.currentUser;
    if (!user || !user.email) return;
    const amount = parseInt(adminAmount);
    if (!amount || amount < 1) {
      setMessage({ type: 'error', text: 'Enter a valid amount.' });
      return;
    }
    setIsGranting(true);
    setMessage(null);
    try {
      const result = await adminGrantCredits(user.uid, user.email, amount);
      setMessage({ type: 'success', text: `Granted ${amount * CREDITS_PER_DOLLAR} credits! New balance: ${result.balance}` });
      refreshCredits();
      window.dispatchEvent(new CustomEvent('credits-updated'));
    } catch (error: any) {
      console.error('Admin grant error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to grant credits.' });
    } finally {
      setIsGranting(false);
    }
  };

  const formatDate = (timestamp: number) => new Date(timestamp * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className={styles.pageContainer}>
      <Navbar />
      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1>Credit Balance</h1>
            <p className={styles.subtitle}>Purchase credits or subscribe for unlimited searches</p>
          </div>

          {/* Current Balance */}
          <div className={styles.balanceSection}>
            <div className={styles.balanceCard}>
              <div className={styles.balanceIcon}>💰</div>
              <div className={styles.balanceInfo}>
                <span className={styles.balanceLabel}>Current Balance</span>
                <span className={styles.balanceAmount}>{loading ? '...' : credits.toLocaleString()}</span>
                <span className={styles.balanceUnit}>credits</span>
              </div>
            </div>
            {subInfo?.hasActiveSubscription && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1))',
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '12px', padding: '12px 16px', marginTop: '12px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <span style={{ fontSize: '20px' }}>⭐</span>
                <div>
                  <div style={{ fontWeight: 600, color: '#d97706', fontSize: '14px' }}>Pro Member — Unlimited Searches</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Renews {subInfo.subscription ? formatDate(subInfo.subscription.currentPeriodEnd) : ''}
                  </div>
                </div>
              </div>
            )}
          </div>

          {message && (
            <div className={`${styles.message} ${styles[message.type]}`}>
              {message.type === 'success' ? '✓' : '!'} {message.text}
            </div>
          )}

          {/* Tab Switcher */}
          <div style={{
            display: 'flex', gap: '0', marginBottom: '24px', borderRadius: '10px',
            overflow: 'hidden', border: '1px solid #e2e8f0',
          }}>
            <button onClick={() => { setActiveTab('pro'); GAUtils.trackBalanceTabSwitch('pro'); }} style={{
              flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
              background: activeTab === 'pro' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#f1f5f9',
              color: activeTab === 'pro' ? '#fff' : '#64748b',
              transition: 'all 0.2s ease',
            }}>⭐ Pro Subscription</button>
            <button onClick={() => { setActiveTab('credits'); GAUtils.trackBalanceTabSwitch('credits'); }} style={{
              flex: 1, padding: '12px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
              background: activeTab === 'credits' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f1f5f9',
              color: activeTab === 'credits' ? '#fff' : '#64748b',
              transition: 'all 0.2s ease',
            }}>💳 Buy Credits</button>
          </div>

          {/* Pro Tab */}
          {activeTab === 'pro' && (
            <div>
              {subInfo?.hasActiveSubscription ? (
                /* Active Subscription View */
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>⭐</div>
                  <h2 style={{ color: '#d97706', fontSize: '22px', marginBottom: '8px' }}>You're a Pro Member!</h2>
                  <p style={{ color: '#64748b', marginBottom: '24px' }}>Enjoy unlimited searches with no credit limits.</p>
                  <div style={{
                    background: '#f8fafc', borderRadius: '12px', padding: '16px', marginBottom: '24px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#64748b' }}>Status</span>
                      <span style={{ color: '#16a34a', fontWeight: 600 }}>Active</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#64748b' }}>Plan</span>
                      <span style={{ color: '#1e293b', fontWeight: 500 }}>${PRO_MONTHLY_PRICE}/month</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#64748b' }}>Next billing</span>
                      <span style={{ color: '#1e293b', fontWeight: 500 }}>{subInfo.subscription ? formatDate(subInfo.subscription.currentPeriodEnd) : ''}</span>
                    </div>
                  </div>
                  <button onClick={handleCancelSubscription} disabled={isCanceling} style={{
                    padding: '10px 24px', fontSize: '13px', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '8px', background: 'transparent', color: '#ef4444', cursor: 'pointer',
                  }}>{isCanceling ? 'Canceling...' : 'Cancel Subscription'}</button>
                </div>
              ) : (
                /* Subscribe View */
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(245,158,11,0.05))',
                    border: '1px solid rgba(251,191,36,0.2)',
                    borderRadius: '16px', padding: '28px', marginBottom: '24px',
                  }}>
                    <div style={{ fontSize: '14px', color: '#fbbf24', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Pro Plan
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '42px', fontWeight: 700, color: '#1e293b' }}>${PRO_MONTHLY_PRICE}</span>
                      <span style={{ fontSize: '16px', color: '#64748b' }}>/month</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Cancel anytime · No commitment</div>
                  </div>

                  <ul style={{
                    margin: '0 auto 24px', padding: '0', listStyle: 'none',
                    fontSize: '15px', lineHeight: '2.2', textAlign: 'left', maxWidth: '300px', color: '#475569',
                  }}>
                    <li>✅ <strong>Unlimited</strong> searches</li>
                    <li>✅ <strong>No ads</strong> experience</li>
                    <li>✅ <strong>Priority</strong> support</li>
                    <li>✅ <strong>Early access</strong> to new features</li>
                    <li>✅ Credits <strong>never consumed</strong> while active</li>
                  </ul>

                  <button onClick={handleSubscribe} disabled={isPurchasing} style={{
                    padding: '14px 32px', fontSize: '16px', fontWeight: 600,
                    border: 'none', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff', cursor: isPurchasing ? 'not-allowed' : 'pointer',
                    width: '100%', maxWidth: '300px',
                    opacity: isPurchasing ? 0.7 : 1,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  }}
                    onMouseOver={(e) => { if (!isPurchasing) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(245,158,11,0.3)'; } }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >{isPurchasing ? 'Redirecting...' : '⭐ Subscribe to Pro'}</button>

                  <p style={{ color: '#64748b', fontSize: '12px', marginTop: '12px' }}>
                    🔒 Secure payment via Stripe · First month just $1.00
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Credits Tab */}
          {activeTab === 'credits' && (
            <div>
              {/* Free Credits Section — admin only */}
              {ADMIN_EMAILS.includes(auth.currentUser?.email || '') && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.04))',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: '14px', padding: '20px', marginBottom: '24px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '14px', color: '#10b981', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Free Credits
                </div>
                <p style={{ color: '#64748b', fontSize: '14px', margin: '8px 0 16px' }}>
                  Watch a short ad to earn 1 free search credit — no limit!
                </p>
                {adMessage && (
                  <div style={{
                    fontSize: '13px', marginBottom: '12px',
                    color: adMessage.type === 'success' ? '#10b981' : '#ef4444',
                  }}>
                    {adMessage.type === 'success' ? '✓' : '!'} {adMessage.text}
                  </div>
                )}
                <button
                  onClick={showRewardedAd}
                  disabled={adLoading}
                  style={{
                    padding: '12px 28px', fontSize: '15px', fontWeight: 600,
                    border: 'none', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: '#fff', cursor: adLoading ? 'not-allowed' : 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    opacity: adLoading ? 0.6 : 1,
                  }}
                  onMouseOver={(e) => { if (!adLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(16,185,129,0.3)'; } }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >{adLoading ? '⏳ Loading Ad...' : '🎬 Watch Ad to Earn 1 Credit'}</button>
              </div>
              )}

              {/* Admin Grant Credits */}
              {isAdmin && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08), rgba(139, 92, 246, 0.04))',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                borderRadius: '14px', padding: '20px', marginBottom: '24px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '14px', color: '#a855f7', fontWeight: 600, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Admin: Free Credits
                </div>
                <p style={{ color: '#64748b', fontSize: '14px', margin: '8px 0 16px' }}>
                  Grant credits without payment (${adminAmount || '0'} = {(parseInt(adminAmount) || 0) * CREDITS_PER_DOLLAR} credits)
                </p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ color: '#a855f7', fontWeight: 600 }}>$</span>
                  <input
                    type="text"
                    value={adminAmount}
                    onChange={(e) => { if (/^\d*$/.test(e.target.value)) setAdminAmount(e.target.value); }}
                    style={{
                      width: '80px', padding: '8px 12px', borderRadius: '8px',
                      border: '1px solid rgba(168, 85, 247, 0.3)', background: 'rgba(168, 85, 247, 0.05)',
                      color: '#1e293b', fontSize: '16px', textAlign: 'center',
                    }}
                  />
                </div>
                <button
                  onClick={handleAdminGrant}
                  disabled={isGranting}
                  style={{
                    padding: '12px 28px', fontSize: '15px', fontWeight: 600,
                    border: 'none', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
                    color: '#fff', cursor: isGranting ? 'not-allowed' : 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    opacity: isGranting ? 0.6 : 1,
                  }}
                  onMouseOver={(e) => { if (!isGranting) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(168,85,247,0.3)'; } }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                >{isGranting ? '⏳ Granting...' : `🎁 Grant ${(parseInt(adminAmount) || 0) * CREDITS_PER_DOLLAR} Credits`}</button>
              </div>
              )}

              <div className={styles.creditExplainer}>
                <h3>🔍 How Credits Work</h3>
                <ul className={styles.creditList}>
                  <li><strong>1 credit = 1 search request</strong></li>
                  <li>Pagination is <strong>free</strong></li>
                </ul>
              </div>

              <div className={styles.pricingInfo}>
                <div className={styles.pricingBadge}>
                  <span className={styles.pricingHighlight}>${MIN_PURCHASE_AMOUNT}</span>
                  <span className={styles.pricingText}>= {CREDITS_PER_DOLLAR} credits</span>
                </div>
                <p className={styles.pricingNote}>That's just ${(1 / CREDITS_PER_DOLLAR).toFixed(2)} per search!</p>
                <p className={styles.pricingNote}>💸 One-time purchases of ${DISCOUNT_THRESHOLD}+ get 10% off.</p>
              </div>

              <div className={styles.amountSection}>
                <h2>Select Amount</h2>
                <div className={styles.amountGrid}>
                  {PRESET_AMOUNTS.map((amount) => (
                    <button key={amount}
                      className={`${styles.amountButton} ${!isCustom && selectedAmount === amount ? styles.selected : ''}`}
                      onClick={() => handleAmountSelect(amount)}
                    >
                      <span className={styles.amountDollar}>${amount}</span>
                      <span className={styles.amountCredits}>
                        {getCreditsForAmount(amount)} credits
                        {amount >= DISCOUNT_THRESHOLD && ' · 10% OFF'}
                      </span>
                    </button>
                  ))}
                </div>

                <div className={styles.customAmount}>
                  <label htmlFor="customAmount">Or enter custom amount:</label>
                  <div className={styles.customInputWrapper}>
                    <span className={styles.currencyPrefix}>$</span>
                    <input type="text" id="customAmount" value={customAmount}
                      onChange={handleCustomAmountChange}
                      placeholder={`${MIN_PURCHASE_AMOUNT}-${MAX_PURCHASE_AMOUNT}`}
                      className={`${styles.customInput} ${isCustom ? styles.active : ''}`} />
                  </div>
                  {isCustom && customAmount && (
                    <span className={styles.customCredits}>
                      = {getCreditsForAmount(parseInt(customAmount) || 0)} credits
                      {(parseInt(customAmount) || 0) >= DISCOUNT_THRESHOLD && ' · 10% OFF applied'}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.summary}>
                <div className={styles.summaryRow}>
                  <span>Amount</span><span className={styles.summaryValue}>${getCurrentAmount()}</span>
                </div>
                {getDiscountForAmount(getCurrentAmount()) > 0 && (
                  <div className={styles.summaryRow}>
                    <span>Discount (10%)</span><span className={styles.summaryValue}>-${getDiscountForAmount(getCurrentAmount()).toFixed(2)}</span>
                  </div>
                )}
                <div className={styles.summaryRow}>
                  <span>Total Charged</span><span className={styles.summaryValue}>${getFinalAmount(getCurrentAmount()).toFixed(2)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span>Credits</span><span className={styles.summaryValue}>{getCreditsForAmount(getCurrentAmount())}</span>
                </div>
                <div className={styles.summaryDivider}></div>
                <div className={styles.summaryRow}>
                  <span>New Balance</span>
                  <span className={styles.summaryTotal}>{loading ? '...' : (credits + getCreditsForAmount(getCurrentAmount())).toLocaleString()}</span>
                </div>
              </div>

              <button className={styles.purchaseButton} onClick={handlePurchase}
                disabled={isPurchasing || !isValidAmount()}>
                {isPurchasing ? (<><span className={styles.spinner}></span>Processing...</>) :
                  (<>Pay ${getFinalAmount(getCurrentAmount()).toFixed(2)} for {getCreditsForAmount(getCurrentAmount())} Credits</>)}
              </button>

              <p className={styles.secureNote}>🔒 Secure payment powered by Stripe</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BalancePage;
